import { P2GAlert, P2GBox, P2GFade, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ToUniqueExtrasList } from '~/apps/smartsend/lib/helpers/ToUniqueExtras';
import { TariffCode } from '../../customs/TariffCode';

import { ProductImage } from '../../product/ProductImage';
import CustomerAddress from '../CustomerAddress';
import { DimensionAndWeightDisplay } from '../DimensionDisplay';
import { getLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';
import { ItemServiceInner } from '../item/ItemService';
import { ItemActionType } from '../item/SetMarketplaceAction';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { PanelButtons } from './PanelButtons';
import { P2GImageMessage } from '~/components/imagemessage';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ECommerceLogo from '~/apps/account-linking/components/ECommerceLogo';
import { getDisplayDimensions } from '~/apps/smartsend/lib/parcels/itemView';
import ConsignmentSummaryParcels from './consignments/ConsignmentSummaryParcels';
import Loading from '~/components/loading/Loading';

interface props {
  close?: () => void;
  setAction(type: ItemActionType);
  item: SmartSendItemApiModel;
  loadingConsignment: boolean;
}

export const ItemOrderPanel: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  if (!props.item || !props.item.BookingDetails || !props.item.BookingDetails.OrderLineId) {
    return (
      <SidePanelSection
        title={
          props.item.MarketplaceOrderLines.map((x) => x.MarketplaceOrderLineId).join(', ') || props.item.Description
        }
      >
        <P2GImageMessage
          image="/theme/smartsend/notifications/no-details-found.png"
          message={t('NO_DETAILS_FOUND_ERROR', { defaultValue: 'No Details Found' })}
          contentKey="image-smartsend-orderpanel-no-details-found"
        >
          <P2GAlert color="info">
            <P2GText>
              {t('NO_BOOKING_DETAILS_TEXT', { defaultValue: "We don't have any booking details for this order." })}
            </P2GText>
          </P2GAlert>
        </P2GImageMessage>
      </SidePanelSection>
    );
  }

  return (
    <>
      <SidePanelSections>
        <SidePanelSection title={t('SHIPMENT_PARCEL_DETAILS_SUBTITLE', 'Shipment Details:')}>
          {props.loadingConsignment ? (
            <Loading />
          ) : props.item.Consignment ? (
            <P2GFade in>
              <ConsignmentSummaryParcels consignment={props.item.Consignment} currencyCode={props.item.CurrencyCode} />
            </P2GFade>
          ) : (
            <DimensionAndWeightDisplay
              dimensions={getDisplayDimensions(props.item)}
              localUnitsEntry={getLocalUnitsEntry(props.item.Meta)}
            />
          )}
        </SidePanelSection>

        {props.item.Type === 'SmartSend' && props.item.MarketplaceOrderLines?.length > 0 && (
          <SidePanelSection title={t('PARCEL_ECOMMERCE_CONTENTS_SUBTITLE', 'eCommerce Order Details:')}>
            {props.item.MarketplaceOrderLines.map((mpi) => (
              <P2GBox key={`${mpi.Id}`} mb={1}>
                <P2GGrid container gap={2} alignItems="center" wrap="nowrap">
                  {mpi.Source?.Id && (
                    <P2GGrid item flexShrink={1}>
                      <ECommerceLogo integration={mpi.Source.Id} style={{ width: '80px' }} />
                    </P2GGrid>
                  )}
                  {mpi.Product.ImageUrl && (
                    <P2GGrid item flexShrink={1}>
                      <ProductImage product={mpi.Product} />
                    </P2GGrid>
                  )}
                  <P2GGrid item flexGrow={1}>
                    <P2GBox>
                      <P2GText>
                        <strong>
                          {mpi.Quantity} x {mpi.Product.Description}
                        </strong>
                      </P2GText>
                    </P2GBox>
                    {(mpi.CustomerReference || mpi.MarketplaceOrderId || mpi.MarketplaceOrderLineId) && (
                      <P2GBox>
                        <P2GText>
                          {t('ORDER_TEXT', { defaultValue: 'Order: ' })}
                          {mpi.CustomerReference ? (
                            <>{mpi.CustomerReference}</>
                          ) : (
                            <>
                              {mpi.MarketplaceOrderId} {mpi.MarketplaceOrderLineId}
                            </>
                          )}
                        </P2GText>
                      </P2GBox>
                    )}
                    {mpi.Product?.Sku && (
                      <P2GBox>
                        <P2GText>
                          <Trans t={t} i18nKey="SKU_TEXT" values={{ sku: mpi.Product.Sku }} defaults="SKU: {{sku}}" />
                        </P2GText>
                      </P2GBox>
                    )}
                    {mpi.Product?.TariffCode && (
                      <P2GBox pt={1}>
                        <TariffCode code={mpi.Product.TariffCode} selectable={true} />
                      </P2GBox>
                    )}
                  </P2GGrid>
                </P2GGrid>
              </P2GBox>
            ))}
          </SidePanelSection>
        )}

        {props.item.Recipient && !!props.item.Recipient.Name && (
          <SidePanelSection title={t('SEND_TO_SUBTITLE', { defaultValue: 'Send To:' })}>
            {/* Masked for the same reason CustomerAddress is - these are the customer's own details. */}
            <P2GText data-sentry-mask>{props.item.Recipient.Name}</P2GText>
            <br />
            {props.item.Recipient.Organisation && (
              <>
                <P2GText data-sentry-mask>{props.item.Recipient.Organisation}</P2GText>
                <br />
              </>
            )}
            <CustomerAddress customer={props.item.Recipient} />
            <P2GGrid container spacing={2} alignItems="center">
              <P2GGrid item>
                <P2GText>{props.item.Recipient.Phone || t('NO_PHONE_FALLBACK', { defaultValue: 'No Phone' })}</P2GText>
              </P2GGrid>
              <P2GGrid item>
                <P2GText>|</P2GText>
              </P2GGrid>
              <P2GGrid item>
                <P2GText>{props.item.Recipient.Email || t('NO_EMAIL_FALLBACK', { defaultValue: 'No Email' })}</P2GText>
              </P2GGrid>
            </P2GGrid>
          </SidePanelSection>
        )}
        {props.item.Sender && !!props.item.Sender.Name && (
          <SidePanelSection title={t('SEND_FROM_SUBTITLE', { defaultValue: 'Send From:' })}>
            <P2GText data-sentry-mask>{props.item.Sender.Name}</P2GText>
            <br />
            {props.item.Sender.Organisation && (
              <>
                <P2GText data-sentry-mask>{props.item.Sender.Organisation}</P2GText>
                <br />
              </>
            )}
            <CustomerAddress customer={props.item.Sender} />
          </SidePanelSection>
        )}
        <SidePanelSection title={t('COURIER_SERVICE_SUBTITLE', { defaultValue: 'Courier & Service:' })}>
          <ItemServiceInner
            itemId={props.item.Id}
            service={props.item.BookingDetails.Service}
            edit={null}
            editService={null}
          />
        </SidePanelSection>
        <SidePanelSection title={t('ADDITONAL_EXTRAS_SUBTITLE', { defaultValue: 'Additional Extras:' })}>
          <P2GText>{ToUniqueExtrasList(t, props.item.Extras) || t('NONE_FALLBACK', { defaultValue: 'None' })}</P2GText>
        </SidePanelSection>
      </SidePanelSections>
      <P2GBox mt={2} />
      <PanelButtons onCancel={props.close} />
    </>
  );
};
